<template>
  <div
    class="diaply-container"
    :style="{
      backgroundImage:
        'url(' + require('@/assets/img/tpwauction-min-blur.jpg') + ')',
    }"
  >
    <h1>Latest Bids</h1>
    <div class="scroll-container" ref="scrollContainer">
      <transition-group name="bid" tag="ul" class="bids-list">
        <li v-for="bid in latestBids" :key="bid.id" class="bid-block flex">
          <div class="bid-number flex align-items-center pl-3 pr-3 text-3xl">
            {{ bid.itemNumber }}
          </div>
          <div class="bid-image flex align-items-center">
            <img :src="bid.itemImage" />
          </div>
          <div class="bid-content flex align-items-center text-left text-3xl">
            <span>
              {{ bid.user.name }} <br />
              {{ formatCurrency(bid.userBid) }}</span
            >
          </div>
        </li>
      </transition-group>
    </div>

    <Notification
      :message="currentNotification"
      :show="showNotification"
      @hidden="handleNotificationHidden"
    />
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { db } from "@/firebase";
import {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import Notification from "@/views/display/components/Notification.vue";
import { formatPrice } from "@/services/formatPrice";

export default {
  name: "LatestBidsView",
  components: { Notification },
  setup() {
    const latestBids = ref([]);
    const latestBid = ref(null);
    const scrollContainer = ref(null);
    let rafId = null;
    let lastTimestamp = null;
    let pauseEnd = 0;
    let scrollAccum = 0;
    const SCROLL_SPEED = 25; // px/s — ~1/3 faster than the old ~16.7 px/s
    const PAUSE_DURATION = 5000;

    const notificationQueue = ref([]);
    const currentNotification = ref("");
    const showNotification = ref(false);
    const pageLoadTimestamp = new Date();
    const processedBids = new Set();
    const processedDonationDocs = new Set();
    const listenedDonationItems = new Set();
    const sponsorDefaultItemIds = new Set();

    const fetchSponsorDefaultIds = async () => {
      const q = query(
        collection(db, "auctionItems"),
        where("isSponsorDefault", "==", true)
      );
      const snapshot = await getDocs(q);
      snapshot.docs.forEach((doc) => sponsorDefaultItemIds.add(doc.id));
    };

    const formatCurrency = (value, currencyCode = "SEK") => {
      const formattedValue = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0, // No decimals for bids
      }).format(value);

      return `${formattedValue} ${currencyCode}`;
    };

    /*const showToast = (bid) => {
      const formattedBid = formatCurrency(bid.userBid); // Format the bid.userBid value

      const toastContent = `
    <div style="display: flex; align-items: center; width: 100%;">
      <img src="${bid.itemImage}" alt="${bid.itemName}" style="width: 150px; margin-right: 10px;" />
      <div class="grid">
        <span class="text-2xl col-12 text-latestbid" style="color: #000000;">${bid.itemNumber}</span>
        <span class="col-12 text-xl" style="color: #000000;"><strong>${bid.user.name}</strong><br/><strong>${formattedBid}</strong></span>
      </div>
    </div>
  `;
      toast(toastContent, {
        position: "bottom-right",
        autoClose: 5000,
        limit: 1,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        dangerouslyHTMLString: true,
        style: { width: "600px" },
      });
    }; */

    const showQueuedNotification = () => {
      if (!showNotification.value && notificationQueue.value.length > 0) {
        currentNotification.value = notificationQueue.value[0];
        showNotification.value = true;
        stopAutoScroll();
      }
    };

    const setupPurchaseListener = (itemId) => {
      if (listenedDonationItems.has(itemId)) return;
      listenedDonationItems.add(itemId);

      const purchasesRef = collection(db, `auctionItems/${itemId}/purchase`);
      const purchasesQuery = query(purchasesRef, orderBy("timestamp", "desc"));

      onSnapshot(purchasesQuery, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type !== "added") return;
          if (processedDonationDocs.has(change.doc.id)) return;
          processedDonationDocs.add(change.doc.id);

          const donation = change.doc.data();
          const donationTime = donation.timestamp?.toDate
            ? donation.timestamp.toDate()
            : new Date((donation.timestamp?.seconds || 0) * 1000);

          if (donationTime <= pageLoadTimestamp) return;

          const formattedPrice = formatPrice(donation.userBid, donation.currency || "SEK");
          const notificationMessage = `
            <h2>NEW DONATION!</h2>
            <h3><span class="stronger">${formattedPrice}</span> by ${donation.user?.name || "Unknown"}</h3>
          `;
          notificationQueue.value.push(notificationMessage);

          nextTick(showQueuedNotification);
        });
      });
    };

    const listenForDonations = () => {
      const donationItemsQuery = query(
        collection(db, "auctionItems"),
        where("isDonationItem", "==", true)
      );

      onSnapshot(donationItemsQuery, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            setupPurchaseListener(change.doc.id);
          }
        });
      });
    };

    const fetchLatestBids = () => {
      const q = query(
        collectionGroup(db, "bids"),
        orderBy("timestamp", "desc"),
        limit(50)
      );

      // Track highest known bid amount per item to avoid duplicate notifications
      const highestKnownBid = {};

      onSnapshot(q, (querySnapshot) => {
        const allBids = querySnapshot.docs
          .filter((doc) => {
            const parentId = doc.ref.parent.parent?.id;
            return !parentId || !sponsorDefaultItemIds.has(parentId);
          })
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => {
            const tsA = a.timestamp?.seconds || 0;
            const tsB = b.timestamp?.seconds || 0;
            if (tsB !== tsA) return tsB - tsA;
            return (b.userBid || 0) - (a.userBid || 0);
          });

        // Deduplicate: remove entries where same item has same bid amount
        // (max-bid update with no actual price change)
        const seenItemAmounts = new Set();
        const bids = allBids.filter((bid) => {
          const key = `${bid.itemID}_${bid.userBid}`;
          if (seenItemAmounts.has(key)) return false;
          seenItemAmounts.add(key);
          return true;
        });

        latestBids.value = bids;

        // Notify only when a bid actually raises the displayed price on an item
        allBids.forEach((bid) => {
          if (processedBids.has(bid.id)) return;
          processedBids.add(bid.id);

          const bidTime = bid.timestamp?.toDate ? bid.timestamp.toDate() : new Date(bid.timestamp?.seconds * 1000 || 0);
          if (bidTime <= pageLoadTimestamp) return;

          const currentHighest = highestKnownBid[bid.itemID] || 0;
          if ((bid.userBid || 0) <= currentHighest) return; // max-bid update, price didn't rise
          highestKnownBid[bid.itemID] = bid.userBid;

          const formattedPrice = formatPrice(bid.userBid, bid.currency || "SEK");
          const notificationMessage = `
            <h2>#${bid.itemNumber} - ${bid.itemName}</h2>
            <h3>New bid of <span class="stronger">${formattedPrice}</span> placed by ${bid.user?.name || 'Unknown'}</h3>
          `;
          notificationQueue.value.push(notificationMessage);

          // Show popup; scroll will resume from current position afterwards.
          // New bids always sit at the top of the list, so they'll be visible
          // when the scroll loop naturally resets at the bottom.
          nextTick(() => {
            showQueuedNotification();
          });
        });
      });
    };

    const performScroll = (timestamp) => {
      rafId = requestAnimationFrame(performScroll);
      const container = scrollContainer.value;
      if (!container) return;

      if (timestamp < pauseEnd) {
        lastTimestamp = null;
        return;
      }

      if (!lastTimestamp) {
        lastTimestamp = timestamp;
        return;
      }

      const elapsed = timestamp - lastTimestamp;
      lastTimestamp = timestamp;
      scrollAccum += (elapsed / 1000) * SCROLL_SPEED;
      const pixels = Math.floor(scrollAccum);

      if (pixels > 0) {
        scrollAccum -= pixels;
        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 1) {
          // Reached the bottom — pause then reset to top
          container.scrollTop = 0;
          lastTimestamp = null;
          pauseEnd = timestamp + PAUSE_DURATION;
        } else {
          container.scrollTop += pixels;
        }
      }
    };

    const startAutoScroll = (delay = 3000) => {
      stopAutoScroll();
      scrollAccum = 0;
      lastTimestamp = null;
      pauseEnd = performance.now() + delay;
      rafId = requestAnimationFrame(performScroll);
    };

    const stopAutoScroll = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const handleNotificationHidden = () => {
      showNotification.value = false;
      notificationQueue.value.shift();
      if (notificationQueue.value.length > 0) {
        setTimeout(() => {
          currentNotification.value = notificationQueue.value[0];
          showNotification.value = true;
        }, 500);
      } else {
        currentNotification.value = "";
        // Resume scrolling from the current position (no reset to top).
        startAutoScroll(0);
      }
    };

    onMounted(async () => {
      await fetchSponsorDefaultIds();
      fetchLatestBids();
      listenForDonations();
      startAutoScroll();
    });

    onBeforeUnmount(() => {
      stopAutoScroll();
    });

    return {
      latestBids,
      latestBid,
      scrollContainer,
      formatCurrency,
      currentNotification,
      showNotification,
      handleNotificationHidden,
    };
  },
};
</script>

<style scoped>
.diaply-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: #2e2e2e; /* Adjust background color as needed */
  background-size: cover;
  color: #fff; /* Text color */
  position: relative;
}

.scroll-container {
  width: 70vh;
  height: 70vh; /* Adjust height as needed */
  overflow: hidden; /* Hide scrollbar */
  position: relative;
}

h1 {
  color: #ffffff !important;
}

.bids-list {
  list-style: none;
  padding: 0;
  z-index: 5;
  width: 90%;
  margin: 0 auto;
}
.bid-block {
  background: rgb(249 249 249 / 19%);
  border-radius: 8px;
  padding: 16px;
  margin: 8px 0;
  transition: transform 0.3s ease;
  display: flex;
}
.bid-content {
  display: flex;
  margin-left: 20px;
  justify-content: center;
  align-items: center;
  font-family: "Playfair Display", serif;
}
.bid-image {
  display: flex;
  justify-content: center;
  img {
    max-width: 250px;
    height: auto;
  }
}

.bid-enter-active,
.bid-leave-active {
  transition: all 0.5s;
}
.bid-enter {
  opacity: 0;
  transform: translateY(-20px);
}
.bid-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.Toastify__toast-body {
  white-space: normal;
}
</style>

<style>
.stronger {
  font-weight: bold;
  color: #c39935;
}
</style>
